import type { AgentThreadView, AgentChatConnection } from '@/_contract/agentChat';
import { AgentChatApiError, type AgentChatBackend } from './agentChatController';
import { isAgentChangeset } from '@/_pure/agentChanges';
import { isAgentPlannedFiles } from '@/_pure/agentPlannedFiles';
import { voiceDiagnostic, ahpStreamDiagnostics } from './voiceDiagnostics';

function isThreadView(value: unknown): value is AgentThreadView {
    return !!value && typeof value === 'object'
        && 'threadId' in value && typeof value.threadId === 'string'
        && 'status' in value && typeof value.status === 'string'
        && (!('diagnostics' in value) || isAhpDiagnostics(value.diagnostics))
        && ['thinking', 'queued', 'working', 'idle', 'error', 'input'].includes(value.status)
        && (!('branch' in value) || typeof value.branch === 'string')
        && (!('error' in value) || typeof value.error === 'string')
        && (!('transport' in value) || value.transport === 'ahp')
        && (!('activity' in value) || value.activity === undefined || typeof value.activity === 'string')
        && (!('activeTurnId' in value) || value.activeTurnId === undefined || typeof value.activeTurnId === 'string')
        && (!('permissionSummary' in value) || typeof value.permissionSummary === 'string')
        && (!('interactionMode' in value) || ['plan', 'interactive', 'autopilot', 'unknown'].includes(String(value.interactionMode)))
        && (!('changesets' in value) || Array.isArray(value.changesets) && value.changesets.every(isAgentChangeset))
        && (!('plannedFiles' in value) || Array.isArray(value.plannedFiles) && value.plannedFiles.every(isAgentPlannedFiles)
            && value.plannedFiles.filter(files => files.current).length <= 1)
        && (!('plans' in value) || Array.isArray(value.plans) && value.plans.every((plan: unknown) =>
            !!plan && typeof plan === 'object' && 'turnId' in plan && typeof plan.turnId === 'string'
            && 'requestId' in plan && typeof plan.requestId === 'string'
            && 'title' in plan && typeof plan.title === 'string' && 'content' in plan && typeof plan.content === 'string'
            && 'pending' in plan && typeof plan.pending === 'boolean'))
        && (!('approvals' in value) || Array.isArray(value.approvals) && value.approvals.every((approval: unknown) =>
            !!approval && typeof approval === 'object' && 'turnId' in approval && typeof approval.turnId === 'string'
            && 'toolCallId' in approval && typeof approval.toolCallId === 'string' && 'title' in approval && typeof approval.title === 'string'
            && 'detail' in approval && typeof approval.detail === 'string' && 'result' in approval && typeof approval.result === 'boolean'
            && 'canApprove' in approval && typeof approval.canApprove === 'boolean'
            && (!('options' in approval) || approval.options === undefined || Array.isArray(approval.options) && approval.options.every((option: unknown) =>
                !!option && typeof option === 'object' && 'id' in option && typeof option.id === 'string' && 'label' in option && typeof option.label === 'string'
                && 'kind' in option && ['approve', 'deny'].includes(String(option.kind))))))
        && (!('inputs' in value) || Array.isArray(value.inputs) && value.inputs.every((input: unknown) =>
            !!input && typeof input === 'object' && 'id' in input && typeof input.id === 'string'
            && (!('message' in input) || input.message === undefined || typeof input.message === 'string')
            && (!('url' in input) || input.url === undefined || typeof input.url === 'string')
            && (!('questions' in input) || input.questions === undefined || Array.isArray(input.questions) && input.questions.every((question: unknown) =>
                !!question && typeof question === 'object' && 'id' in question && typeof question.id === 'string'
                && 'kind' in question && ['text', 'number', 'integer', 'boolean', 'single-select', 'multi-select'].includes(String(question.kind))
                && 'message' in question && typeof question.message === 'string'
                && (!('title' in question) || question.title === undefined || typeof question.title === 'string')
                && (!['single-select', 'multi-select'].includes(String(question.kind)) || 'options' in question && Array.isArray(question.options)
                    && question.options.every((option: unknown) => !!option && typeof option === 'object'
                        && 'id' in option && typeof option.id === 'string' && 'label' in option && typeof option.label === 'string'))))))
        && 'entries' in value && Array.isArray(value.entries)
        && value.entries.every((entry: unknown) => !!entry && typeof entry === 'object'
            && 'role' in entry && ['user', 'agent'].includes(String(entry.role))
            && 'content' in entry && typeof entry.content === 'string'
            && (!('speechContent' in entry) || typeof entry.speechContent === 'string')
            && (!('responseContent' in entry) || typeof entry.responseContent === 'string')
            && 'at' in entry && typeof entry.at === 'string');
}

function isAhpDiagnostics(value: unknown): value is NonNullable<AgentThreadView['diagnostics']> {
    if (!value || typeof value !== 'object') return false;
    const data = value as Record<string, unknown>;
    if (!['sessionChannel', 'sessionTitle', 'provider', 'chatChannel', 'approvalMode', 'observedAt']
        .every(key => typeof data[key] === 'string')
        || !Array.isArray(data.workspace) || !data.workspace.every(item => typeof item === 'string')
        || typeof data.turnCount !== 'number' || !Number.isSafeInteger(data.turnCount) || data.turnCount < 0) return false;
    if (data.latestTurn === undefined) return true;
    if (!data.latestTurn || typeof data.latestTurn !== 'object') return false;
    const turn = data.latestTurn as Record<string, unknown>;
    return ['id', 'state', 'startedAt'].every(key => typeof turn[key] === 'string')
        && typeof turn.toolCalls === 'number' && Number.isSafeInteger(turn.toolCalls) && turn.toolCalls >= 0;
}

async function request(init: RequestInit, threadId?: string): Promise<AgentThreadView> {
    const response = await fetch(`/api/agent${threadId ? `?threadId=${encodeURIComponent(threadId)}` : ''}`, {
        ...init, cache: 'no-store',
    });
    const value: unknown = await response.json();
    if (!response.ok) throw new AgentChatApiError(value && typeof value === 'object'
        && 'error' in value && typeof value.error === 'string' ? value.error : `Request failed (${response.status}).`, response.status);
    if (!isThreadView(value)) throw new Error('Invalid agent chat response.');
    return value;
}

export const agentChatBackend: AgentChatBackend = {
    async initialize(signal) {
        const response = await fetch('/api/agent?mode=connection', { signal, cache: 'no-store' });
        const value: unknown = await response.json();
        if (!response.ok) throw new AgentChatApiError(value && typeof value === 'object' && 'error' in value
            && typeof value.error === 'string' ? value.error : 'Chat connection failed.', response.status);
        if (!value || typeof value !== 'object' || !('transport' in value) || !['legacy', 'ahp'].includes(String(value.transport))
            || !('sessions' in value) || !Array.isArray(value.sessions) || !value.sessions.every(session =>
                session && typeof session === 'object' && typeof session.threadId === 'string' && typeof session.title === 'string')) throw new Error('Invalid chat connection response.');
        if ('development' in value && value.development !== undefined) {
            const development = value.development;
            if (value.transport !== 'ahp' || !development || typeof development !== 'object'
                || !('id' in development) || typeof development.id !== 'string' || !/^dev-[a-z0-9][a-z0-9-]{0,26}$/.test(development.id)
                || !('branch' in development) || development.branch !== `cfde/${development.id}`) throw new Error('Invalid development session binding.');
        }
        return value as AgentChatConnection;
    },
    read: (threadId, signal) => request({ signal }, threadId),
    send: (instruction, threadId, signal) => request({
        method: 'POST', signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction, ...(threadId ? { threadId } : {}) }),
    }),
    command: (threadId, command, signal) => request({
        method: 'POST', signal, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threadId, command }),
    }),
    watch(threadId, onView, onError) {
        const started = Date.now();
        ahpStreamDiagnostics.update('connecting');
        const source = new EventSource(`/api/agent?mode=events&threadId=${encodeURIComponent(threadId)}`);
        const diagnostic = (event: 'stream-open' | 'stream-error' | 'stream-invalid' | 'stream-bridge-error' | 'stream-close') =>
            voiceDiagnostic(event, { elapsedMs: Date.now() - started, readyState: source.readyState });
        source.onopen = () => { diagnostic('stream-open'); ahpStreamDiagnostics.update('connected'); };
        source.onmessage = event => {
            try {
                const value: unknown = JSON.parse(event.data);
                if (!isThreadView(value)) throw new Error('Invalid streamed chat state.');
                onView(value);
                ahpStreamDiagnostics.update('connected');
            } catch (error) {
                diagnostic('stream-invalid');
                ahpStreamDiagnostics.update('invalid frame');
                source.close();
                onError(error instanceof Error ? error.message : 'Invalid streamed chat state.');
            }
        };
        source.onerror = () => {
            diagnostic('stream-error');
            ahpStreamDiagnostics.update('reconnecting');
            onError('Connection interrupted. Reconnecting to Forge...');
        };
        source.addEventListener('bridgeError', () => {
            diagnostic('stream-bridge-error');
            ahpStreamDiagnostics.update('bridge interrupted');
            onError('Forge stream interrupted. Reconnecting...');
        });
        return () => { diagnostic('stream-close'); source.close(); ahpStreamDiagnostics.update('closed'); };
    },
};
